import React from 'react';
import { useData } from '../lib/DataContext.jsx';
import { KpiCard, SectionTitle, BandPill, DataBarCell, Chip, ProvenanceFooter, ProvenanceBadge } from '../components/ui/kits.jsx';
import { HBars, Lollipop } from '../components/charts/charts.jsx';
import { ProjectCode } from '../components/ui/ProjectCode.jsx';
import { fmt, fmtInt } from '../lib/format.js';

const SEV_COLOR = { High: '#c8452f', Medium: '#d98a1f', Low: '#0a63b0', Info: '#6b7691' };

export default function DataHealth() {
  const { data } = useData();
  if (!data) return null;
  const h = data.dataHealth;

  const dimColor = (pct) => pct >= 90 ? '#1f8a4c' : pct >= 60 ? '#5aa06a' : pct >= 40 ? '#d98a1f' : '#c8452f';

  const submissionSummary = h.submissionSummary;
  const donutTotal = submissionSummary.total || (submissionSummary.Complete + submissionSummary.Partial + submissionSummary.Pending);

  return (
    <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 18 }} data-testid="page-health">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
        <KpiCard testid="kpi-completeness" hero label="Overall Completeness" value={`${fmt(h.overall, 0)}%`} sub={`${h.fullProjects} full · ${h.incompleteProjects} incomplete`} />
        <KpiCard testid="kpi-incomplete" label="Incomplete Records" value={fmtInt(h.incompleteProjects)} sub="Missing ≥1 dimension" />
        <KpiCard testid="kpi-anomalies" label="Anomalies Flagged" value={String(h.anomalies.reduce((a, b) => a + b.count, 0))}
          sub={Object.entries(h.severityCounts).map(([k, v]) => `${k}: ${v}`).join(' · ')} />
        <KpiCard testid="kpi-pending-partners" label="Partner Submissions Pending"
          value={String((submissionSummary.Partial || 0) + (submissionSummary.Pending || 0))}
          sub={`${submissionSummary.Complete}/${donutTotal} complete`} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 14 }}>
        <div className="card" style={{ padding: 18 }}>
          <SectionTitle right={<ProvenanceBadge kind="live" />}>Completeness by Dimension</SectionTitle>
          <HBars
            testid="dh-dimensions"
            rows={h.dimensions.map((d) => ({
              label: d.field,
              value: d.pct,
              max: 100,
              color: dimColor(d.pct),
              sub: d.note,
              valueText: `${d.pct}% · ${fmtInt(d.have)}/${fmtInt(d.total)}`,
            }))}
          />
        </div>

        <div className="card" style={{ padding: 18 }}>
          <SectionTitle right={<ProvenanceBadge kind="model" note="Submission status is heuristic from completeness — MIS timestamps pending" />}>Partner Submission Status</SectionTitle>
          <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
            <DonutChart data={[
              { k: 'Complete', v: submissionSummary.Complete, color: '#1f8a4c' },
              { k: 'Partial', v: submissionSummary.Partial, color: '#d98a1f' },
              { k: 'Pending', v: submissionSummary.Pending, color: '#c8452f' },
            ]} total={donutTotal} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { k: 'Complete', color: '#1f8a4c', v: submissionSummary.Complete },
                { k: 'Partial', color: '#d98a1f', v: submissionSummary.Partial },
                { k: 'Pending', color: '#c8452f', v: submissionSummary.Pending },
              ].map((r) => (
                <div key={r.k} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                  <span style={{ width: 12, height: 12, background: r.color, borderRadius: 3 }} />
                  <span style={{ fontWeight: 600 }}>{r.k}</span>
                  <span style={{ marginLeft: 'auto', fontWeight: 700, color: r.color }} className="tabular">{r.v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 18 }}>
        <SectionTitle right={<ProvenanceBadge kind="live" note="Data collection agency per project from ResearchStudies.csv" />}>
          Data Collection Agencies · programme rollup
        </SectionTitle>
        <AgencyRollup />
      </div>

      <div className="card" style={{ padding: 18 }}>
        <SectionTitle right={<ProvenanceBadge kind="model" note="Anomaly heuristics — not yet a DB rule" />}>Anomaly Register</SectionTitle>
        <table className="pv-table">
          <thead><tr><th>Severity</th><th>Type</th><th style={{ textAlign: 'right' }}>Count</th><th>Detail</th><th>Recommended Action</th></tr></thead>
          <tbody>
            {h.anomalies.map((a, i) => (
              <tr key={i} data-testid={`anomaly-${i}`}>
                <td><Chip color={SEV_COLOR[a.sev]}>{a.sev}</Chip></td>
                <td style={{ fontWeight: 700, fontSize: 12 }}>{a.type}</td>
                <td className="tabular" style={{ textAlign: 'right' }}>{a.count}</td>
                <td style={{ fontSize: 12 }}>{a.desc}</td>
                <td style={{ fontSize: 12, color: 'var(--ink-2)' }}>{a.action}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card" style={{ padding: 18 }}>
        <SectionTitle right={<ProvenanceBadge kind="live" />}>Tool Coverage</SectionTitle>
        <Lollipop
          testid="dh-tool-coverage"
          rows={h.toolCoverage.map((t) => ({
            label: t.tool,
            value: (t.assessed / t.total) * 100,
            max: 100,
            color: '#0a63b0',
            valueText: `${t.assessed} / ${t.total}`,
          }))}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 14 }}>
        <div className="card" style={{ padding: 18 }}>
          <SectionTitle right={<ProvenanceBadge kind="model" note="Follow-up list derived from modelled submission status" />}>Partners Needing Follow-up</SectionTitle>
          <div className="scroll-y" style={{ maxHeight: 380 }}>
            <table className="pv-table">
              <thead><tr><th>Partner</th><th style={{ textAlign: 'right' }}>Prj</th><th>Status</th><th>Completeness</th><th>Missing</th></tr></thead>
              <tbody>
                {h.partnerSubmission.filter((p) => p.status !== 'Complete').slice(0, 16).map((p) => (
                  <tr key={p.partner}>
                    <td style={{ fontSize: 12 }}>{p.partner}</td>
                    <td className="tabular" style={{ textAlign: 'right' }}>{p.projects}</td>
                    <td><Chip color={p.status === 'Partial' ? '#d98a1f' : '#c8452f'}>{p.status}</Chip></td>
                    <td><DataBarCell value={p.completeness} max={100} colorHint={p.completeness >= 60 ? '#5aa06a' : '#c8452f'} /></td>
                    <td style={{ fontSize: 11, color: 'var(--ink-3)' }}>{(p.missing || []).slice(0, 3).map((m) => <Chip key={m}>{m}</Chip>)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card" style={{ padding: 18 }}>
          <SectionTitle right={<ProvenanceBadge kind="live" />}>Least-complete Records</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
            {h.worstProjects.slice(0, 12).map((p) => (
              <div key={p.code} style={{ padding: 12, border: '1px solid var(--line)', borderRadius: 10, background: 'var(--surface-2)' }} data-testid={`worst-${p.code}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div style={{ fontWeight: 800, fontSize: 12 }}><ProjectCode code={p.code} /></div>
                  <span className="tabular" style={{ fontSize: 12, fontWeight: 700, color: p.pct < 60 ? '#c8452f' : '#d98a1f' }}>{p.pct}%</span>
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--ink-2)', marginTop: 4 }}>{p.partner || '—'} · {p.state || '—'}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                  {(p.missing || []).slice(0, 4).map((m) => (
                    <span key={m} style={{ fontSize: 10, background: '#fbe7e3', color: '#b62e1c', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>{m}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <ProvenanceFooter text="Submission status is currently modelled from completeness heuristics. Production wiring pending: SurveyCTO / MIS submission timestamps. Budget field remains the largest data gap (37% coverage)." />
    </div>
  );
}

function AgencyRollup() {
  const { beneficiaries } = useData();
  if (!beneficiaries || !beneficiaries.agencies || beneficiaries.agencies.length === 0) {
    return <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>Agency data unavailable.</div>;
  }
  const max = Math.max(1, ...beneficiaries.agencies.map((a) => a.projects));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} data-testid="agency-rollup">
      <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
        <b>{beneficiaries.agencies.length}</b> independent evaluation firms surveyed <b>{fmtInt(beneficiaries.coverage.projectsWithAgency)}</b> projects across the programme.
      </div>
      {beneficiaries.agencies.map((a, i) => {
        const pct = (a.projects / max) * 100;
        const colors = ['#042a54', '#0a63b0', '#2e7cbf', '#6a8fb5', '#8a4b3a', '#9b2d20', '#e23d28', '#d98a1f', '#5aa06a', '#1f8a4c', '#6b7691'];
        const color = colors[i % colors.length];
        return (
          <div key={a.agency} style={{ display: 'grid', gridTemplateColumns: '260px 1fr 140px', gap: 12, alignItems: 'center' }} data-testid={`agency-${i}`}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink)' }}>
              {a.agency}
              <div style={{ fontSize: 10.5, color: 'var(--ink-4)', fontWeight: 500, marginTop: 1 }}>
                {a.statesCount} state{a.statesCount === 1 ? '' : 's'} · {a.states.slice(0, 3).join(', ')}{a.states.length > 3 ? '…' : ''}
              </div>
            </div>
            <div style={{ position: 'relative', height: 14, background: 'var(--line-2)', borderRadius: 4, overflow: 'hidden' }}>
              <div className="anim-grow" style={{
                width: `${pct}%`, height: '100%', background: color, borderRadius: 4,
              }} />
            </div>
            <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--ink-3)' }} className="tabular">
              <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--ink)' }}>{a.projects}</span> projects
              <div style={{ fontSize: 10.5, color: 'var(--ink-4)' }}>{fmtInt(a.respondents)} respondents</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DonutChart({ data, total, size = 130 }) {
  const R = size / 2 - 6;
  const r = R - 22;
  const cx = size / 2, cy = size / 2;
  let acc = 0;
  const t = total || 1;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {data.map((d) => {
        const start = (acc / t) * 2 * Math.PI;
        acc += d.v;
        const end = (acc / t) * 2 * Math.PI;
        if (d.v === 0) return null;
        const large = end - start > Math.PI ? 1 : 0;
        const p = (a, rr) => [cx + rr * Math.sin(a), cy - rr * Math.cos(a)];
        const [x1, y1] = p(start, R);
        const [x2, y2] = p(end, R);
        const [x3, y3] = p(end, r);
        const [x4, y4] = p(start, r);
        return <path key={d.k} d={`M ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${r} ${r} 0 ${large} 0 ${x4} ${y4} Z`} fill={d.color} stroke="#fff" strokeWidth="1.5" />;
      })}
      <text x={cx} y={cy - 2} textAnchor="middle" fontSize="18" fontWeight="800" fill="var(--ink)" className="tabular">{total}</text>
      <text x={cx} y={cy + 14} textAnchor="middle" fontSize="9.5" fill="var(--ink-3)" fontWeight="700">PARTNERS</text>
    </svg>
  );
}
